<template>
  <div
    class="create-account-view"
    @keyup.enter="onEnterPressed"
  >
    <div class="floating-card">
      <div
        v-if="!showSuccessMessage"
        class="card-text"
      >
        Create an Account
      </div>
      <div
        v-show="showSuccessMessage"
        class="success-wrapper"
      >
        Your account was created successfully.
        Please check your inbox for an email to finish up your account creation.
      </div>
      <TextInput
        v-if="!showSuccessMessage"
        v-model="firstName"
        label="First Name"
        :show-clear="true"
        :required="true"
      />
      <TextInput
        v-if="!showSuccessMessage"
        v-model="lastName"
        label="Last Name"
        :required="true"
      />
      <label
        v-if="!showSuccessMessage"
        class="input-label"
      >Date Of Birth</label>
      <DatePicker
        v-if="!showSuccessMessage"
        id="date-of-birth"
        v-model="dateOfBirthLocal"
        class="input date-picker"
        input-format="MM-dd-yyyy"
        :required="true"
        @update:modelValue="onDateOfBirthChanged"
      />
      <TextInput
        v-if="!showSuccessMessage"
        v-model="email"
        label="Email"
        :required="true"
      />
      <TextInput
        v-if="!showSuccessMessage"
        v-model="password"
        label="Password"
        type="password"
        :required="true"
      />
      <div v-if="!showSuccessMessage">
        Password Requirements:
      </div>
      <ul v-if="!showSuccessMessage">
        <li>
          At least 6 characters long
          <CloseSvg
            v-if="!passwordHasCorrectLength"
            class="close"
          />
          <CheckmarkSvg v-if="passwordHasCorrectLength" />
        </li>
        <li>
          At least one non alphanumeric character
          <CloseSvg
            v-if="!passwordHasNonAlphanumericCharacter"
            class="close"
          />
          <CheckmarkSvg v-if="passwordHasNonAlphanumericCharacter" />
        </li>
        <li>
          At least one digit
          <CloseSvg
            v-if="!passwordHasNumber"
            class="close"
          />
          <CheckmarkSvg v-if="passwordHasNumber" />
        </li>
      </ul>
      <div
        v-show="showErrorMessage"
        class="error-text"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="!showSuccessMessage"
        class="button-wrapper"
      >
        <LockingButton
          class="locking-button"
          :disabled="!formValid"
          :locked="loading"
          :text="'Create Account'"
          @click="createAccount"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import { MiscellaneousService } from '@/services/miscellaneous.service'
import { CreateAccountMessage } from '@/@types/messages/CreateAccountMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import DatePicker from 'vue3-datepicker'
import moment from 'moment'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import CheckmarkSvg from '@/components/svgs/CheckmarkSvg.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'

export default defineComponent({
  name: 'CreateAccount',
  components: {
    CheckmarkSvg,
    CloseSvg,
    DatePicker,
    LockingButton,
    TextInput
  },
  setup() {
    const miscellaneousService = inject(PROVIDER_CONSTANTS.miscellaneousService) as MiscellaneousService
    return {
      miscellaneousService
    }
  },
  data() {
    let dateOfBirthLocal: Date|undefined

    return {
      dateOfBirthLocal,
      dateOfBirthUtc: '',
      email: '',
      errorMessage: '',
      firstName: '',
      lastName: '',
      loading: false,
      password: '',
      showErrorMessage: false,
      showSuccessMessage: false
    }
  },
  computed: {
    formValid(): boolean {
      return this.dateOfBirthUtc.trim() !== '' &&
             this.email.trim() !== '' &&
             this.firstName.trim() !== '' &&
             this.lastName.trim() !== '' &&
             this.passwordHasCorrectLength &&
             this.passwordHasNonAlphanumericCharacter &&
             this.passwordHasNumber
    },
    passwordHasCorrectLength(): boolean {
      return this.password.trim().length >= 6
    },
    passwordHasNonAlphanumericCharacter(): boolean {
      return this.password.trim().length > 0 && !/^[a-zA-Z0-9]+$/.test(this.password)
    },
    passwordHasNumber(): boolean {
      return /\d/.test(this.password)
    }
  },
  methods: {
    createAccount(): void {
      if (this.loading) return

      this.showErrorMessage = false
      this.loading = true

      const createAccountMessage = new CreateAccountMessage(this.dateOfBirthUtc, this.email, this.firstName, this.lastName, this.password)
      this.miscellaneousService.createAccount(createAccountMessage).then(() => {
        this.loading = false
        this.showSuccessMessage = true
      }).catch(error => {
        const errorsResponse = error as ErrorsResponse

        this.loading = false
        this.errorMessage = errorsResponse.errors.map(e => e.message).join("\r\n")
        this.showErrorMessage = true
      })
    },
    onDateOfBirthChanged(): void {
      const element = document.getElementById('date-of-birth')
      element?.blur()

      this.dateOfBirthUtc = this.dateOfBirthLocal ? moment.utc(this.dateOfBirthLocal).format() : ''
    },
    onEnterPressed(): void {
      if (this.formValid)
        this.createAccount()
    }
  }
})
</script>

<style lang="scss">

.create-account-view .date-picker {
  @include input-text-field;
  cursor: pointer;
  margin-bottom: 15px;
}

</style>

<style scoped lang="scss">

.create-account-view {
  @include sac-floating-card-view;

  > .floating-card .input-label {
    @include input-text-field-label;
  }

  li > svg {
    margin-left: 5px;
    fill: $Color-font-valid;

    &.close {
      height: 12px;
      width: 12px;
      fill: $Color-font-error;
    }
  }
}

</style>
